import { inject, Injectable } from "@angular/core";

import { AuthService, NetworkStatusService, OfflineCacheService } from "src/app/core";
import { Permissions } from "src/assets/config/config";
import { environment } from "src/environments/environment";

/**
 * Why a booking may not be queued. Each maps to its own message — "cannot book offline" without the
 * reason leaves a clerk to guess, and the four reasons want four different actions from them.
 */
export type OfflineBookingBlock = "disabled" | "role" | "notTour" | "stalePricing";

export interface OfflineBookingDecision {
  allowed: boolean;
  block?: OfflineBookingBlock;
}

/** The cache key fragment for the pricing endpoint — `reservationprice/get`. */
const PRICING_URL_FRAGMENT = "reservationprice/get";

/**
 * How old cached prices may be and still be booked against.
 *
 * Deliberately much tighter than the 24h `expireMinutes` the cache itself enforces. That number
 * bounds how wrong data may be when there is nothing better to show; this one bounds how wrong it may
 * be when somebody is about to be **charged** against it, and those are not the same question.
 *
 * **The number wants finance's sign-off** (T-17 §6). Four hours covers a shift without spanning a
 * price change made overnight; nobody has agreed it yet, and it is one constant to move when they do.
 */
const MAX_PRICING_AGE_MS = 4 * 60 * 60_000;

/**
 * @description
 * The gate on offline bookings. Everything it refuses, it refuses for a reason a clerk can act on.
 *
 * The order below is the order of certainty, not convenience: the flag first because nothing else
 * matters while it is off, then who is asking, then what they are booking, and only then the slow
 * question that needs a storage read.
 *
 * ## What each refusal protects
 *
 * | | |
 * | --- | --- |
 * | `disabled` | The feature flag. **This is T-03's gate** — see `environment.offlineBooking`. |
 * | `role` | Offline booking is a counter capability. The public surface shares these routes, so the check is on the role rather than the URL (T-17 §5). |
 * | `notTour` | Events have finite seats, checked client-side against numbers that go stale the moment the network does. Two terminals booking offline would oversell a hall. TOUR has no such limit — that is what makes any of this tractable. |
 * | `stalePricing` | Booking at yesterday's price is worse than not booking. |
 */
@Injectable({ providedIn: "root" })
export class OfflineBookingPolicyService {
  readonly #auth = inject(AuthService);
  readonly #cache = inject(OfflineCacheService);
  readonly #network = inject(NetworkStatusService);

  /** True only where every rule agrees. `body` is the reservation payload about to be submitted. */
  async evaluate(body: Record<string, unknown>): Promise<OfflineBookingDecision> {
    if (!environment.offlineBooking) return { allowed: false, block: "disabled" };

    const role = this.#auth.userRole();
    if (!role || !Permissions.BookingServices.includes(role)) return { allowed: false, block: "role" };

    // `reservationTypeCode` is the form's own declaration of what it is booking, set where the form is
    // built. `OfficialInstitutions` is refused along with `EVENT`: the allow-list is TOUR, and a third
    // value nobody has reasoned about offline should fail closed.
    if (body["reservationTypeCode"] !== "TOUR") return { allowed: false, block: "notTour" };

    if (!(await this.#pricingIsFresh())) return { allowed: false, block: "stalePricing" };

    return { allowed: true };
  }

  /** True when the booking should be diverted to the queue rather than reported as a failure. */
  isOffline(): boolean {
    return this.#network.isOffline();
  }

  /**
   * Absent pricing counts as stale. A terminal that never cached a price cannot have shown the clerk
   * one either, so whatever total is on screen did not come from the server this session.
   *
   * **Known limit: this is the newest entry for the endpoint, not the entry this booking priced
   * against.** Each building and tour type is a separate cache entry, so a clerk who opened a
   * different form recently leaves a fresh entry that vouches for the older one actually on screen —
   * the rule catches a terminal that has been offline all day, not a single stale price among fresh
   * ones. Closing that gap means matching the exact pricing request this form made, which is the
   * coupling `newestUpdatedAtFor()` was written to avoid; it wants doing when T-18 has the booking
   * carry the prices it was quoted.
   *
   * **Second known limit: this covers `reservationprice/get` and nothing else, while three other
   * price-bearing endpoints are now cached for 24 hours.** `CompaniesService.getTourismCompanyCost()`
   * and `GroupOffersService.getAll()` are the two discount tables the categories step applies on top
   * of the prices, and `ProductsService.getPaged()` / `getProductsByTypeId()` carry the shop prices.
   * All four are money, all four can legitimately be six times older than the four hours this gate
   * allows for the ticket price itself, and a booking priced against them passes. Each of those call
   * sites reasons that "a live server is always preferred" — true online, and not the situation this
   * gate is for. Extending the check to them will start refusing bookings that succeed today, so it
   * belongs with the finance sign-off `MAX_PRICING_AGE_MS` is already waiting on rather than in a
   * patch alongside it.
   */
  async #pricingIsFresh(): Promise<boolean> {
    // The *oldest* cached pricing entry, not the newest — see `oldestUpdatedAtFor`. Prices are cached
    // per building and tour type, and letting the freshest entry speak for all of them is how a form
    // opened minutes ago comes to vouch for another form's day-old prices.
    const updatedAt = await this.#cache.oldestUpdatedAtFor(PRICING_URL_FRAGMENT);
    if (!updatedAt) return false;

    return Date.now() - updatedAt <= MAX_PRICING_AGE_MS;
  }
}
